import { APP_CONFIG } from "@/constants/config.constants";
import { tracker } from "@/constants/tracker.constants";
import { AgentRole } from "@/graphql/generated/graphql";
import { isDev } from "@/helpers/environment/environment.helpers";
import {
  getCommonPayload,
  getSignedInAsPayload,
} from "@/helpers/tracker/tracker.helpers";
import { useCurrentCompany } from "@/hooks/useCurrentCompany/useCurrentCompany";
import { useCurrentCompanyBilling } from "@/hooks/useCurrentCompanyBilling/useCurrentCompanyBilling";
import { useCurrentUser } from "@/hooks/useCurrentUser/useCurrentUser";
import { useGetFeaturesFlags } from "@/hooks/useGetFeaturesFlags/useGetFeaturesFlags";
import { ICompanyBillingPeriod, TRACKER_ENVIRONMENT } from "@aircall/tracker";
import { getBrowser } from "@dashboard/library";
import Cookies from "js-cookie";
import { useEffect, useRef } from "react";

/**
 * This code will be removed after some time.
 * It removes Rudderstack v3 cookies from the browser,
 * which came from aircall.io. These cookies are not
 * set anymore, as Rudderstack has been configured in
 * aircall-website project to use v2 cookies. This was
 * creating a issue that caused millions of Datadog logs.
 *
 * More info in DAT-130 Jira ticket
 */
/* istanbul ignore next */
function removeRudderstackV3Cookies() {
  const cookies = Cookies.get() ?? {};

  Object.entries(cookies).forEach(([name, value]) => {
    if (value.includes("RS_ENC_v3")) {
      Cookies.remove(name, { domain: ".aircall.io", path: "/" });
    }
  });
}

export function useInitTracker(): void {
  const { disableFrontendTracking } = useGetFeaturesFlags();

  const { currentCompany } = useCurrentCompany();
  const { currentCompanyBilling } = useCurrentCompanyBilling();
  const { currentUser } = useCurrentUser();

  const trackerInitialized = useRef(false);

  useEffect(() => {
    if (disableFrontendTracking || isDev()) {
      return;
    }

    // CurrentUser and CurrentCompany are required to initialize the tracker.
    if (!currentUser || !currentCompany) {
      return;
    }

    if (!trackerInitialized.current) {
      removeRudderstackV3Cookies();

      try {
        tracker.init();
        trackerInitialized.current = true;
      } catch (e) {
        console.error("error thrown at initialization of Rudderstack", e);
      }
    }

    try {
      tracker.identify({
        ...getCommonPayload(),
        ...getSignedInAsPayload(),
        device_details: getBrowser(),
        user_id: parseInt(currentUser.ID),
        company_id: currentCompany.id,
        company_name: currentCompany.name,
        created_at: currentCompany.createdAt,
        device: "",
        company_plan: currentCompany.plan,
        language: currentUser.language?.replace("-", "_") ?? "",
        company: {
          billing_period:
            currentCompanyBilling?.billingPeriod as ICompanyBillingPeriod,
          country: currentCompanyBilling?.address?.country as string,
          created_at: currentCompany.createdAt,
          last_plan_change: currentCompany.lastTimePlanChanged ?? undefined,
          is_trial: false,
          tier: currentCompany.tierLevel,
        },
        email: currentUser.email,
        environment: APP_CONFIG.environment as unknown as TRACKER_ENVIRONMENT,
        is_admin: currentUser.role === AgentRole.admin,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        // do type casting here because we don't have `created_at` in currentUser
        // but it is required by TrackerIdentification
      });
    } catch (e) {
      console.error("Error thrown during tracker identification:", e);
    }
  }, [
    disableFrontendTracking,
    currentUser,
    currentCompany,
    currentCompanyBilling,
  ]);
}
